import cx from 'classnames';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import { useState } from 'react';
import { ArrowLink, Link, RadioButton, Tag } from '@bwoty-web/ui-kit';

import OnRequestPopup from './OnRequestPopup';

import * as PriceMatrixLogUtils from '../../utils/logUtils/PriceMatrixLogUtils';

const RoomGroupRow = ({
  selectedRoomGroup,
  roomGroup,
  labels,
  keyIndex,
  selectRoomGroup,
  showRoomTypeLink,
}) => {
  const [isOnRequestPopupOpen, setIsOnRequestPopupOpen] = useState(false);

  const selectRoomGroupFunc = () => {
    if (roomGroup === selectedRoomGroup) {
      return;
    }
    PriceMatrixLogUtils.priceMatrixRoomGroupClick();
    selectRoomGroup(roomGroup);
  };

  const clickOnRequestLink = () => {
    PriceMatrixLogUtils.priceMatrixOnRequestLinkClicked();
    setIsOnRequestPopupOpen(!isOnRequestPopupOpen);
  };

  const getBedTypeDescription = ({ description, type }) => {
    const bedTypeDescription = labels[type.toLowerCase()];
    if (bedTypeDescription) return bedTypeDescription;
    return description;
  };

  const renderBedType = (bedTypesList) =>
    bedTypesList.map((bedType) => (
      <span
        key={uniqid(`${bedType.quantity}-${bedType.description}-`)}
        className="price-matrix-table__amenity"
      >
        {`${bedType.quantity} ${getBedTypeDescription(bedType)}`}
      </span>
    ));

  const renderRoomQuantity = (quantity) => {
    return (
      <span
        className="price-matrix-table__room-quantity"
        key={uniqid('room-quantity-')}
      >
        ({quantity} {labels.numberOfSuffix})
      </span>
    );
  };

  const renderRoomList = () => {
    let showOnRequestLabel = false;

    const openRoomModal = (roomId) => {
      if (roomId && typeof window !== 'undefined' && window) {
        window.postMessage(`cmd.open.popup:${roomId}`);
      }
    };

    const roomId = roomGroup?.roomType?.wvId || roomGroup?.roomType?.externalId;

    const roomList = roomGroup.rooms.map((room) => {
      const roomText = (
        <div
          key={uniqid('room-list-')}
          className="price-matrix-table__room-description"
        >
          {roomId && showRoomTypeLink ? (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link onClick={() => openRoomModal(roomId)}>
              {room.description}
            </Link>
          ) : (
            room.description
          )}
          {renderRoomQuantity(room.quantity)}
        </div>
      );

      if (!room.isAvailable && !showOnRequestLabel) showOnRequestLabel = true;
      return roomText;
    });

    return (
      <div>
        <div>
          {roomList}
          {showOnRequestLabel && (
            <ArrowLink
              className="price-matrix-table__on-request"
              onClick={clickOnRequestLink}
            >
              {labels.availableOnRequest}
            </ArrowLink>
          )}
          {roomGroup.hasCharges && (
            <Tag className="price-matrix-table__room-tags" small>
              {labels.paymentLabel}
            </Tag>
          )}
        </div>
        {(roomGroup.bedTypes || roomGroup.amenities.length > 0) && (
          <div className="price-matrix-table__amenities">
            {roomGroup.bedTypes ? renderBedType(roomGroup.bedTypes) : null}
            {roomGroup.amenities &&
              roomGroup.amenities.map((amenity) => {
                return (
                  <div className="price-matrix-table__amenity" key={amenity.id}>
                    <span>{amenity.name}</span>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  };

  const renderNonRefundableLabel = () => {
    return (
      roomGroup.hasCharges && (
        <Tag className="price-matrix-table__room-tags" small>
          {labels.paymentLabel}
        </Tag>
      )
    );
  };

  const { totalPrice, isDiscounted } = roomGroup.priceBreakDowns;
  const isSelectedGroup =
    selectedRoomGroup.bookingEngineRoomGroupKey ===
    roomGroup.bookingEngineRoomGroupKey;

  const roomGroupClasses = cx('price-matrix-table__row', {
    'price-matrix-table__row--selected': isSelectedGroup,
    'price-matrix-table__row--first': keyIndex === 0,
  });

  const totalPriceClasses = cx('price-matrix-table__total-price', {
    'price-matrix-table__total-price--has-discount': isDiscounted,
  });

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={roomGroupClasses}
      onClick={selectRoomGroupFunc}
      data-room-index={keyIndex}
    >
      <div className="price-matrix-table__left-column">
        <RadioButton checked={isSelectedGroup} onChange={() => {}} />
      </div>
      <div className="price-matrix-table__middle-column">
        {renderRoomList()}
      </div>
      <div className="price-matrix-table__right-column">
        {renderNonRefundableLabel()}
        <div className={totalPriceClasses}>{totalPrice}</div>
      </div>
      <OnRequestPopup
        labels={labels}
        isOpen={isOnRequestPopupOpen}
        hidePopup={() => setIsOnRequestPopupOpen(false)}
      />
    </div>
  );
};

RoomGroupRow.propTypes = {
  roomGroup: PropTypes.shape({
    roomType: PropTypes.shape({
      wvId: PropTypes.number,
      externalId: PropTypes.string,
    }),
    rooms: PropTypes.arrayOf(PropTypes.shape({})),
    bedTypes: PropTypes.arrayOf(PropTypes.shape({})),
    amenities: PropTypes.arrayOf(PropTypes.shape({})),
    hasCharges: PropTypes.bool,
    specialOffer: PropTypes.shape({}),
    priceBreakDowns: PropTypes.shape({
      totalPrice: PropTypes.string,
      totalDiscount: PropTypes.string,
      isDiscounted: PropTypes.bool,
    }),
    bookingEngineRoomGroupKey: PropTypes.string,
  }).isRequired,
  labels: PropTypes.shape({
    kingbed: PropTypes.string.isRequired,
    queenbed: PropTypes.string.isRequired,
    fullbed: PropTypes.string.isRequired,
    twinbed: PropTypes.string.isRequired,
    sofabed: PropTypes.string.isRequired,
    bunkbed: PropTypes.string.isRequired,
    availableOnRequest: PropTypes.string.isRequired,
    paymentLabel: PropTypes.string.isRequired,
    numberOfSuffix: PropTypes.string.isRequired,
  }).isRequired,
  selectRoomGroup: PropTypes.func.isRequired,
  selectedRoomGroup: PropTypes.shape({
    bookingEngineRoomGroupKey: PropTypes.string,
  }).isRequired,
  keyIndex: PropTypes.number.isRequired,
  showRoomTypeLink: PropTypes.bool.isRequired,
};

export default RoomGroupRow;
